// Mahnwesen: Einstellungen (Stufen, Tage, Gebühren, Texte), Mahnvorschlag, Bestätigung in einem Schritt, Mahnungen
// zum Drucken, Mahnsperre je Kunde oder Posten, Verlauf je Kunde. Nichts wird automatisch gemahnt.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zAmountOrZeroCents, zDay, zPartnerRef, zReason } from './common'

const READ = 'finance.dunning.read'
const RUN = 'finance.dunning.run'
const SETTINGS = 'finance.dunning.settings'

export const zDunningLevel = z.strictObject({
  level: z.int().min(1).max(9),
  /** ab so vielen Tagen über Fälligkeit wird diese Stufe vorgeschlagen */
  afterDays: z.int({ error: 'Bitte die Tage angeben.' }).min(0, { error: 'Die Tage dürfen nicht negativ sein.' }).max(365),
  feeCents: zAmountOrZeroCents,
  title: z.string({ error: 'Bitte einen Titel eingeben.' }).trim().min(2, { error: 'Bitte einen Titel eingeben.' }).max(80),
  text: z.string({ error: 'Bitte einen Text eingeben.' }).trim().min(10, { error: 'Bitte einen Text eingeben (mindestens 10 Zeichen).' }).max(2000),
})
export type DunningLevel = z.output<typeof zDunningLevel>

export const zDunningSettings = z.object({
  /** Tage nach Fälligkeit, in denen noch nicht gemahnt wird */
  graceDays: z.int(),
  /** offene Beträge darunter werden nicht gemahnt */
  minAmountCents: z.int(),
  /** Zahlungsfrist der Mahnung in Tagen */
  paymentDays: z.int(),
  /** Mindestabstand zwischen zwei Mahnungen desselben Postens in Tagen */
  intervalDays: z.int(),
  levels: z.array(zDunningLevel),
})
export type DunningSettings = z.output<typeof zDunningSettings>

export const dunningSettingsGet = defineContract({
  name: 'finance.dunning.settingsGet',
  title: 'Einstellungen des Mahnwesens anzeigen',
  description: 'Mahnstufen mit Tagen, Gebühren und Texten, Schonfrist, Mindestbetrag und Zahlungsfrist.',
  llm: {
    description: 'Liefert die Einstellungen des Mahnwesens: Stufen (ab wie vielen Tagen, Gebühr in Cent, Titel, Text), Schonfrist, Mindestbetrag, Zahlungsfrist der Mahnung, Abstand zwischen Mahnungen. Verändert nichts.',
    keywords: ['Mahnstufen', 'Mahngebühr', 'Mahntext', 'Einstellungen Mahnwesen'],
  },
  input: z.strictObject({}),
  output: zDunningSettings.extend({ version: z.int(), isDefault: z.boolean() }),
  permission: READ,
  risk: 'read',
})

export const dunningSettingsUpdate = defineContract({
  name: 'finance.dunning.settingsUpdate',
  title: 'Einstellungen des Mahnwesens ändern',
  description: 'Ändert Mahnstufen, Gebühren, Texte und Fristen. Bereits bestätigte Mahnungen bleiben, wie sie sind.',
  llm: {
    description:
      'Ändert die Einstellungen des Mahnwesens. `levels` ersetzt ALLE Stufen (lückenlos ab 1, Tage aufsteigend). ' +
      'Gebühren in Cent. Platzhalter im Text: {kunde}, {betrag}, {frist}. Wirkt auf künftige Mahnvorschläge – ' +
      'bestätigte Mahnungen ändern sich nicht.',
    keywords: ['Mahnstufen ändern', 'Mahngebühr ändern', 'Mahntext ändern'],
  },
  input: z.strictObject({
    graceDays: z.int().min(0, { error: 'Die Schonfrist darf nicht negativ sein.' }).max(90).optional(),
    minAmountCents: zAmountOrZeroCents.optional(),
    paymentDays: z.int().min(1, { error: 'Die Zahlungsfrist beträgt mindestens einen Tag.' }).max(90).optional(),
    intervalDays: z.int().min(0).max(90).optional(),
    levels: z.array(zDunningLevel).min(1, { error: 'Bitte mindestens eine Mahnstufe angeben.' }).max(6).optional(),
    expectedVersion: z.int().optional(),
  }),
  output: zDunningSettings.extend({ version: z.int(), isDefault: z.boolean() }),
  permission: SETTINGS,
  risk: 'write',
})

const zProposalItem = z.object({
  openItemId: zId,
  documentId: zId.nullable(),
  number: z.string().nullable(),
  documentDate: z.string().nullable(),
  dueDate: z.string(),
  daysOverdue: z.int(),
  amountCents: z.int(),
  openCents: z.int(),
  /** Stufe aus bestätigten Mahnungen (0 = noch nicht gemahnt) */
  currentLevel: z.int(),
  /** Stufe, die dieser Lauf dem Posten gäbe */
  nextLevel: z.int(),
  lastDunnedAt: z.string().nullable(),
})

export const zDunningProposal = z.object({
  partner: zPartnerRef,
  /** höchste Stufe der Posten = Stufe der Mahnung */
  level: z.int(),
  title: z.string(),
  feeCents: z.int(),
  items: z.array(zProposalItem),
  openCents: z.int(),
  /** Guthaben des Kunden (Rechnungskorrekturen, Überzahlungen) – als Hinweis, nicht abgezogen */
  creditsCents: z.int(),
  totalCents: z.int(),
  dueDate: z.string(),
  hints: z.array(z.string()),
})
export type DunningProposal = z.output<typeof zDunningProposal>

export const dunningPropose = defineContract({
  name: 'finance.dunning.propose',
  title: 'Mahnvorschlag anzeigen',
  description: 'Wer zu mahnen wäre: je Kunde die überfälligen Posten, die Stufe und die Gebühr – zum Prüfen, Zeile für Zeile.',
  llm: {
    description:
      'Berechnet den Mahnvorschlag zum Stichtag: je Kunde die überfälligen Forderungen (Beleg, Fälligkeit, Tage ' +
      'überfällig, offener Betrag in Cent), die bisherige und die vorgeschlagene Mahnstufe, die Gebühr laut ' +
      'Einstellungen und die neue Zahlungsfrist. Gesperrte Kunden und Posten stehen mit ihrem Grund unter `blocked`. ' +
      'Verändert nichts – es wird nie automatisch gemahnt. Bestätigt wird mit finance.dunning.run.',
    keywords: ['Mahnvorschlag', 'Mahnlauf', 'wen mahnen', 'überfällige Kunden', 'Zahlungserinnerung'],
  },
  input: z.strictObject({ runDate: zDay.optional(), partnerId: zId.optional() }),
  output: z.object({
    runDate: z.string(),
    proposals: z.array(zDunningProposal),
    blocked: z.array(z.object({ partner: zPartnerRef, number: z.string().nullable(), openCents: z.int(), daysOverdue: z.int(), reason: z.string(), scope: z.enum(['partner', 'item']) })),
    totals: z.object({ partnerCount: z.int(), itemCount: z.int(), openCents: z.int(), feeCents: z.int() }),
  }),
  permission: READ,
  risk: 'read',
})

export const dunningRun = defineContract({
  name: 'finance.dunning.run',
  title: 'Mahnlauf bestätigen',
  description: 'Bestätigt die geprüften Mahnungen in einem Schritt: je Kunde eine Mahnung mit Nummer, Stufe, Gebühr und Zahlungsfrist.',
  llm: {
    description:
      'Bestätigt einen Mahnlauf: Für jeden Eintrag in `notices` (Kunde, seine überfälligen Posten, Gebühr in Cent) ' +
      'entsteht EINE Mahnung mit Nummer; die Posten erreichen die vorgeschlagene Stufe, eine Gebühr wird als offener ' +
      'Posten gebucht. Es gilt nur, was finance.dunning.propose zum selben Stichtag vorschlägt – weggelassene Posten ' +
      'und Kunden werden nicht gemahnt. Versendet nichts von selbst: Die Mahnungen stehen danach zum Drucken bereit; per E-Mail mit finance.dunning.sendRun bzw. sendNotice. ' +
      'Zurücknehmen: finance.dunning.cancelRun.',
    keywords: ['Mahnlauf bestätigen', 'mahnen', 'Mahnung erstellen', 'Zahlungserinnerung erstellen'],
  },
  input: z.strictObject({
    runDate: zDay.optional(),
    note: z.string().trim().max(300).nullish(),
    notices: z
      .array(
        z.strictObject({
          partnerId: zId,
          openItemIds: z.array(zId).min(1, { error: 'Bitte mindestens einen Posten wählen.' }).max(200),
          /** Gebühr dieser Mahnung in Cent; ohne Angabe laut Einstellungen */
          feeCents: zAmountOrZeroCents.optional(),
        }),
      )
      .min(1, { error: 'Bitte mindestens eine Mahnung wählen.' })
      .max(200),
  }),
  output: z.object({
    runId: zId,
    runDate: z.string(),
    notices: z.array(z.object({ id: zId, number: z.string(), partner: zPartnerRef, level: z.int(), title: z.string(), feeCents: z.int(), totalCents: z.int(), itemCount: z.int() })),
    message: z.string(),
  }),
  permission: RUN,
  risk: 'financial',
  effects: { reversible: true },
  limits: { maxItems: 200, itemCount: (input) => input.notices.length },
})

export const dunningCancelRun = defineContract({
  name: 'finance.dunning.cancelRun',
  title: 'Mahnlauf zurücknehmen',
  description: 'Nimmt einen bestätigten Mahnlauf zurück: Die Posten fallen auf ihre vorige Stufe, offene Mahngebühren werden ausgebucht.',
  llm: {
    description:
      'Nimmt einen bestätigten Mahnlauf vollständig zurück (irrtümlich gemahnt): Die Mahnungen zählen nicht mehr, die ' +
      'Posten haben wieder ihre vorige Stufe, noch offene Mahngebühren werden ausgebucht. Braucht einen Grund; nichts ' +
      'wird gelöscht. Nicht möglich, wenn eine Gebühr des Laufs schon bezahlt ist.',
    keywords: ['Mahnlauf zurücknehmen', 'Mahnung stornieren', 'irrtümlich gemahnt'],
  },
  input: z.strictObject({ runId: zId, reason: zReason }),
  output: z.object({ runId: zId, noticeCount: z.int(), message: z.string() }),
  permission: RUN,
  risk: 'destructive',
})

const zNoticeRow = z.object({
  id: zId,
  runId: zId,
  number: z.string().nullable(),
  runDate: z.string(),
  partner: zPartnerRef,
  level: z.int(),
  title: z.string(),
  feeCents: z.int(),
  openCents: z.int(),
  totalCents: z.int(),
  dueDate: z.string().nullable(),
  itemCount: z.int(),
  /** final | cancelled */
  status: z.string(),
  createdByName: z.string().nullable(),
})

export const dunningNotices = defineContract({
  name: 'finance.dunning.notices',
  title: 'Mahnungen anzeigen',
  description: 'Bestätigte Mahnungen – alle oder der Verlauf eines Kunden.',
  llm: {
    description:
      'Listet bestätigte Mahnungen, neueste zuerst: Nummer, Datum, Kunde, Stufe, Gebühr, gemahnter Betrag in Cent, ' +
      'Zahlungsfrist und ob der Lauf zurückgenommen wurde. Mit `partnerId` der Mahnverlauf eines Kunden. Die Mahnung ' +
      'zum Drucken liefert finance.dunning.notice.',
    keywords: ['Mahnungen', 'Mahnverlauf', 'Mahnhistorie', 'wann gemahnt'],
  },
  input: z.strictObject({ partnerId: zId.optional(), runId: zId.optional(), limit: z.int().min(1).max(200).default(100) }),
  output: z.object({ rows: z.array(zNoticeRow) }),
  permission: READ,
  risk: 'read',
})

export const dunningNotice = defineContract({
  name: 'finance.dunning.notice',
  title: 'Mahnung anzeigen',
  description: 'Eine Mahnung mit Anschrift, Text, Posten, Gebühr und Summe – so, wie sie gedruckt wird.',
  llm: {
    description: 'Liefert eine bestätigte Mahnung vollständig für Anzeige und Druck: Absender, Empfänger, Titel, Text, jeder gemahnte Posten mit Belegnummer, Fälligkeit und offenem Betrag in Cent, Gebühr, Summe, Zahlungsfrist.',
    keywords: ['Mahnung drucken', 'Mahnung anzeigen', 'Zahlungserinnerung'],
  },
  input: z.strictObject({ id: zId }),
  output: zNoticeRow.extend({
    body: z.string(),
    creditsCents: z.int(),
    items: z.array(z.object({ openItemId: zId, documentId: zId.nullable(), number: z.string().nullable(), documentDate: z.string().nullable(), dueDate: z.string().nullable(), daysOverdue: z.int(), amountCents: z.int(), openCents: z.int(), level: z.int() })),
    recipient: z.object({ name: z.string(), lines: z.array(z.string()), number: z.string().nullable() }),
    sender: z.object({ name: z.string(), lines: z.array(z.string()), contact: z.array(z.string()), bank: z.array(z.string()), legalLine: z.string(), register: z.string().nullable(), managingDirectors: z.string().nullable(), vatId: z.string().nullable(), taxNumber: z.string().nullable() }),
    demo: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
})

export const dunningBlockSet = defineContract({
  name: 'finance.dunning.blockSet',
  title: 'Mahnsperre setzen',
  description: 'Sperrt einen Kunden oder einen einzelnen Posten für das Mahnen – mit Grund.',
  llm: {
    description:
      'Setzt eine Mahnsperre: entweder für den ganzen Kunden (`partnerId`) oder für EINEN offenen Posten ' +
      '(`openItemId`) – genau eines von beiden. Braucht einen Grund (Reklamation, Ratenzahlung vereinbart). Gesperrtes ' +
      'erscheint im Mahnvorschlag nur noch unter „gesperrt“. Aufheben: finance.dunning.blockLift.',
    keywords: ['Mahnsperre', 'nicht mahnen', 'Mahnstopp', 'Reklamation', 'Ratenzahlung'],
  },
  input: z.strictObject({ partnerId: zId.optional(), openItemId: zId.optional(), reason: zReason }).refine((value) => (value.partnerId === undefined) !== (value.openItemId === undefined), {
    error: 'Bitte entweder den Kunden oder einen offenen Posten angeben.',
  }),
  output: z.object({ id: zId, scope: z.enum(['partner', 'item']), message: z.string() }),
  permission: RUN,
  risk: 'write',
  effects: { reversible: true },
})

export const dunningBlockLift = defineContract({
  name: 'finance.dunning.blockLift',
  title: 'Mahnsperre aufheben',
  description: 'Hebt eine Mahnsperre auf. Die Sperre bleibt mit Grund und Zeitraum im Verlauf stehen.',
  llm: {
    description: 'Hebt eine bestehende Mahnsperre auf (`id` aus finance.dunning.blocks). Der Kunde bzw. Posten erscheint danach wieder im Mahnvorschlag. Die Sperre bleibt im Verlauf sichtbar.',
    keywords: ['Mahnsperre aufheben', 'wieder mahnen'],
  },
  input: z.strictObject({ id: zId, reason: z.string().trim().max(300).nullish() }),
  output: z.object({ id: zId, message: z.string() }),
  permission: RUN,
  risk: 'write',
})

export const dunningBlocks = defineContract({
  name: 'finance.dunning.blocks',
  title: 'Mahnsperren anzeigen',
  description: 'Bestehende und aufgehobene Mahnsperren mit Grund, wer sie gesetzt hat und wann.',
  llm: {
    description: 'Listet Mahnsperren: Kunde oder Posten, Grund, gesetzt von und am, aufgehoben am. Mit `partnerId` nur die eines Kunden; `activeOnly` (Standard) nur bestehende.',
    keywords: ['Mahnsperren', 'wer ist gesperrt', 'Mahnstopp'],
  },
  input: z.strictObject({ partnerId: zId.optional(), activeOnly: z.boolean().default(true) }),
  output: z.object({
    rows: z.array(
      z.object({
        id: zId,
        scope: z.enum(['partner', 'item']),
        partner: zPartnerRef,
        openItemId: zId.nullable(),
        number: z.string().nullable(),
        reason: z.string(),
        setByName: z.string().nullable(),
        setAt: z.date(),
        liftedAt: z.date().nullable(),
        liftedByName: z.string().nullable(),
        liftReason: z.string().nullable(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
})
